import { computed, onBeforeUnmount } from "vue";
import { useQuery, useQueryClient } from "@tanstack/vue-query";
import type { Socket } from "socket.io-client";
import AgentService from "@/services/ai/agent";
import { AGENT_CONVERSATIONS_KEY } from "@/composables/agent/useAgentConversations";
import { getSocket } from "@/utils/socket";
import type { AgentReplyDraftEvent, AgentUnreadReplyDraft } from "@/dtos/Agent.dto";

export const AGENT_UNREAD_REPLY_DRAFTS_KEY = ["agent", "reply-drafts", "unread"] as const;
const REPLY_DRAFT_EVENT = "agent:reply-draft";

/**
 * The AI reply drafts the rep hasn't opened yet, each in its contact's CRM assistant thread,
 * for the assistant bubble. Loaded on start (so drafts that came while the rep was away
 * still show) and refreshed whenever the server announces a new or rewritten draft.
 *
 * `onDraft` hears every announcement, e.g. to reload the thread when it's the open one.
 */
export function useReplyDraftInbox(onDraft?: (event: AgentReplyDraftEvent) => void) {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: AGENT_UNREAD_REPLY_DRAFTS_KEY,
    queryFn: async (): Promise<AgentUnreadReplyDraft[]> => {
      const result = await AgentService.listUnreadReplyDrafts();
      if (!result.data.status) throw new Error(result.data.msg);
      return (result.data.data ?? []) as AgentUnreadReplyDraft[];
    },
    // Drafts also get sent or dismissed from the conversation panels; this catches those.
    refetchInterval: 60_000,
  });

  const unread = computed(() => query.data.value ?? []);

  const handleDraft = (event: AgentReplyDraftEvent) => {
    queryClient.invalidateQueries({ queryKey: AGENT_UNREAD_REPLY_DRAFTS_KEY });
    queryClient.invalidateQueries({ queryKey: AGENT_CONVERSATIONS_KEY });
    onDraft?.(event);
  };

  // Live updates are a nicety: the bubble still fills from the list without the socket.
  let socket: Socket | null = null;
  try {
    socket = getSocket();
    socket.on(REPLY_DRAFT_EVENT, handleDraft);
  } catch (err) {
    console.error("AI reply draft updates are unavailable:", err);
  }
  onBeforeUnmount(() => socket?.off(REPLY_DRAFT_EVENT, handleDraft));

  /** Re-read the list, e.g. after a draft was sent, dismissed or its thread opened. */
  const refresh = () => queryClient.invalidateQueries({ queryKey: AGENT_UNREAD_REPLY_DRAFTS_KEY });

  return { unread, refresh };
}
